import Image from 'next/image';

import FadeIn from '@/components/ui/FadeIn';
import StarRating from '@/components/ui/StarRating';
import Text from '@/components/ui/Text';
import type { GoogleReview } from '@/lib/google/getGoogleReviews';

const GoogleIcon = () => (
  <svg
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    className="shrink-0"
  >
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const ReviewCard = ({ review, index, ariaLabelStars }: ReviewCardProps) => {
  const { authorName, authorPhotoUrl, rating, text, relativeTime } = review;
  const initial = authorName.charAt(0).toUpperCase();

  return (
    <FadeIn delay={index * 0.05}>
      <div className="relative flex flex-col gap-5 p-7 bg-surface rounded-xs h-full overflow-hidden shadow-[0_2px_16px_rgba(0,0,0,0.06)]">
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="absolute top-4 right-5 w-14 h-14 text-secondary/10 pointer-events-none select-none"
        >
          <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" />
          <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" />
        </svg>

        <StarRating rating={rating} ariaLabel={ariaLabelStars} />

        <Text
          as="p"
          variant="paragraph"
          className="mb-0! relative z-10 line-clamp-5"
        >
          {text}
        </Text>

        <div className="flex items-center gap-3 pt-4 border-t border-text/10">
          {authorPhotoUrl ? (
            <Image
              src={authorPhotoUrl}
              alt={authorName}
              width={40}
              height={40}
              className="rounded-full shrink-0 ring-2 ring-secondary/20"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-secondary/15 flex items-center justify-center shrink-0">
              <span className="text-secondary font-semibold text-sm leading-none">
                {initial}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <Text as="p" variant="cardTitle" className="text-sm! truncate">
              {authorName}
            </Text>
            <Text as="p" variant="paragraph" className="mb-0! text-sm!">
              {relativeTime}
            </Text>
          </div>
          <GoogleIcon />
        </div>
      </div>
    </FadeIn>
  );
};

export default ReviewCard;

type ReviewCardProps = {
  review: GoogleReview;
  index: number;
  ariaLabelStars: string;
};
